import { type OnereDTO } from '@framework/api-client/api/inquiries-compile/schemas/onereDTO.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { CurrencyRender } from '@framework/ui/components/currency/CurrencyRender.tsx';
import { type ColumnsType, type ColumnType } from 'antd/es/table';
import { BurdenConstraintTag } from './BurdenConstraintTag.tsx';
import { hasDistinctRecipientOffice } from './hasDistinctRecipientOffice.ts';

const burdenColumnsBase = {
  burden: {
    title: t('summary-payments.onere', 'Onere'),
    key: 'descrizione',
    render: (_, record) =>
      record.descrizione ?? record.tipoOnere?.denominazione ?? null,
  },
  recipientOffice: {
    title: t('summary-payments.ufficio-destinatario', 'Ufficio destinatario'),
    key: 'ufficioDestinatario',
    width: '25%',
    render: (_, record) =>
      hasDistinctRecipientOffice(record)
        ? record.datiRiepilogo?.denominazioneUfficioDestinatarioPagamento
        : null,
  },
  attachment: {
    title: t('summary-payments.allegato', 'Allegato'),
    key: 'allegato',
    render: (_, record) =>
      record.tipoOnere?.macroTipoOnere?.agidCodiceAllegato ?? null,
  },
  constraint: {
    title: t('summary-payments.vincolo', 'Vincolo'),
    key: 'vincolo',
    render: (_, record) => (
      <BurdenConstraintTag tipoVincolo={record.tipoOnere?.tipoVincolo} />
    ),
  },
  amount: {
    title: t('summary-payments.importo', 'Importo'),
    key: 'valore',
    align: 'right',
    render: (_, record) => <CurrencyRender value={record.valore} />,
  },
} satisfies Record<string, ColumnType<OnereDTO>>;

/**
 * Restituisce le colonne della tabella oneri.
 *
 * La colonna dell'ufficio destinatario compare solo se almeno un onere ha un
 * destinatario diverso dal responsabile: se nessuno ce l'ha la colonna
 * sarebbe interamente vuota. Non dipende dalla sorgente del riepilogo, perché
 * `datiRiepilogo` è valorizzato sempre, non solo per il Backoffice.
 */
export function getBurdenColumns(burdens: OnereDTO[]): ColumnsType<OnereDTO> {
  const showRecipientOffice = burdens.some(hasDistinctRecipientOffice);
  const detailWidth = showRecipientOffice ? '15%' : '20%';

  return [
    {
      ...burdenColumnsBase.burden,
      width: showRecipientOffice ? '30%' : '40%',
    },
    ...(showRecipientOffice ? [burdenColumnsBase.recipientOffice] : []),
    { ...burdenColumnsBase.attachment, width: detailWidth },
    { ...burdenColumnsBase.constraint, width: detailWidth },
    { ...burdenColumnsBase.amount, width: detailWidth },
  ];
}
